import {
  Component,
  inject,
  signal,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, debounceTime, map, switchMap, tap } from 'rxjs';
import { ButtonComponent } from '@aprova-digital/design-system';

import { AuthRepositoryInstruction } from '../../../builder/instructions/repositories/auth.repository.instructions';
import { VerifyChangePasswordTokenServiceInstruction } from '../../../builder/instructions/services/verify-change-password-token.service.instruction';
import { VerifyUserPasswordServiceInstruction } from '../../../builder/instructions/services/verify-user-password.service.instruction';
import { ResetPasswordServiceInstruction } from '../../../builder/instructions/services/reset-password.service.instruction';
import { Types } from '../../../domain/types.domain';

import {
  Rule,
  getPasswordRules,
  getConfirmPasswordRules,
} from '../../utils/password.rules';
import { AppHandler } from '../../app.handler';
import {
  BoxErrorComponent,
  IconComponent,
  InputComponent,
  LoadingSpinnerComponent,
  RecaptchaComponent,
} from '../../../@ads/components';
import { FormRulesBoxComponent } from '../../components/form-rules-box/form-rules-box.component';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    LoadingSpinnerComponent,
    RecaptchaComponent,
    InputComponent,
    IconComponent,
    ButtonComponent,
    BoxErrorComponent,
    FormRulesBoxComponent,
  ],
  providers: [
    new AuthRepositoryInstruction(),
    new ResetPasswordServiceInstruction(),
    new VerifyChangePasswordTokenServiceInstruction(),
    new VerifyUserPasswordServiceInstruction(),
  ],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss',
})
export class ChangePasswordComponent {
  private readonly _verifyChangePasswordTokenService = inject(
    Types.VerifyChangePasswordTokenService
  );
  private readonly _verifyUserPasswordService = inject(
    Types.VerifyUserPasswordService
  );
  private readonly _resetPasswordService = inject(Types.ResetPasswordService);

  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);

  private readonly _appHandler = inject(AppHandler);
  private readonly _authRepository = inject(Types.AuthRepository);

  public cityIdentity: string = this._route.snapshot.data['city']['identidade'];
  public cityConnection: string =
    this._route.snapshot.data['city']['connection'];
  public hideRecaptcha: boolean = false;
  public cityId: number = this._route.snapshot.data['city']['index'];

  public type: string = 'change';
  public email: string = '';
  public token: string = '';
  public customUrlRedirect: string = '';

  public rules: Rule[] = [];
  public passwordRules: Rule[] = getPasswordRules();
  public confirmPasswordRules: Rule[] = getConfirmPasswordRules();

  public showPassword = signal(false);
  public showConfirmPassword = signal(false);
  public verifyingLastPassword = signal(false);
  public saving = signal(false);
  public passwordChanged = signal(false);
  public focusedField = signal<'password' | 'confirmPassword' | null>(null);
  public redirectTimeout: any;

  public form = new FormGroup({
    password: new FormControl('', {
      nonNullable: true,
      validators: Validators.required,
    }),
    confirmPassword: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    recaptchaReactive: new FormControl(''),
  });

  get isPasswordValid() {
    return this.passwordRules.every((rule) => rule.valid);
  }

  get isConfirmPasswordValid() {
    return this.confirmPasswordRules.every((rule) => rule.valid);
  }

  get shouldKeepPasswordRulesVisible() {
    return !this.isPasswordValid || !this.isConfirmPasswordValid;
  }

  protected readonly data$ = combineLatest({
    params: this._route.queryParams.pipe(
      tap(({ email, token, type, customUrlRedirect }) => {
        if (!email || !token) {
          this.redirectToLogin();
          return;
        }

        this.customUrlRedirect = customUrlRedirect || '';

        this._verifyChangePasswordTokenService
          .handle({
            connection: this.cityConnection,
            email,
            token,
          })
          .then(() => {
            this.email = email;
            this.token = token;
            this.type = type || 'change';
          })
          .catch(() => {
            this.redirectToLogin();
          });
      })
    ),
    city: this._route.data.pipe(
      map(({ city }) => ({
        cityIdentity: city.identidade,
        cityConnection: city.connection,
        cityIndex: city.index,
      })),
      tap((data) => {
        this.cityIdentity = data.cityIdentity;
        this.cityConnection = data.cityConnection;
        this.cityId = data.cityIndex;
      })
    ),
    config: this._route.data.pipe(
      switchMap(({ city }) =>
        this._authRepository.getConfigByCityId(city.index)
      )
    ),
  });

  ngOnInit(): void {
    this._appHandler.setBackButtonHome(false);
    this._appHandler.setLoading(false);

    this.passwordRules.push({
      key: 'lastPassword',
      label: 'A senha não pode ser igual à senha anterior.',
      valid: false,
      regex: '',
    });

    this.form.controls.password.valueChanges
      .pipe(
        tap(() => {
          this.validatePasswordRules();
        }),
        debounceTime(500)
      )
      .subscribe(() => {
        this.verifyLastPassword();
      });
  }

  redirectToLogin(): void {
    if (this.redirectTimeout) {
      clearTimeout(this.redirectTimeout);
    }

    this._appHandler.setLoading(true);

    if (this.customUrlRedirect) {
      this._router.navigate([`/${this.cityIdentity}`], {
        queryParams: {
          customUrlRedirect: this.customUrlRedirect,
        },
      });
    } else {
      this._router.navigate([`/${this.cityIdentity}`]);
    }
  }

  onSubmit(): void {
    this._appHandler.setError(false);

    if (this.form.valid) {
      this.saving.set(true);

      this._resetPasswordService
        .handle({
          city: this.cityIdentity,
          connection: this.cityConnection,
          email: this.email,
          token: this.token,
          password: this.form.controls.password.value,
          recaptchaReactive: this.form.controls.recaptchaReactive.value,
        })
        .then(() => {
          this.saving.set(false);
          this.passwordChanged.set(true);

          this.redirectTimeout = setTimeout(() => {
            this.redirectToLogin();
          }, 5000);
        })
        .catch(() => {
          this.saving.set(false);
          this._appHandler.setError(true, {
            message: 'A operação não pôde ser concluída.',
            description:
              'Por favor, revise seus dados antes de fazer uma nova tentativa. Se o problema persistir, entre em contato com nossa equipe de suporte.',
            actionText: 'Tentar novamente',
            action: () => {
              this._appHandler.clearError();
            },
          });
        });
    }
  }

  toggleInputPassword(): void {
    this.showPassword.update((val) => !val);
  }

  toggleInputConfirmPassword(): void {
    this.showConfirmPassword.update((val) => !val);
  }

  showPasswordRulesTooltip(
    input: 'password' | 'confirmPassword',
    _event: FocusEvent
  ): void {
    this.focusedField.set(input);
    if (input === 'confirmPassword') {
      this.rules = this.confirmPasswordRules;
      this.validateConfirmPasswordRules();
    } else {
      this.rules = this.passwordRules;
      this.validatePasswordRules();
    }
  }

  hidePasswordRulesTooltip(): void {
    setTimeout(() => {
      const active = document.activeElement;
      const isPasswordField =
        active?.closest?.('[data-change-password-field="password"]') ||
        active?.closest?.('[data-change-password-field="confirmPassword"]');

      if (isPasswordField) {
        return;
      }

      if (this.shouldKeepPasswordRulesVisible) {
        return;
      }

      this.focusedField.set(null);
    }, 200);
  }

  validatePasswordRules(): void {
    const password = this.form.controls['password'].value;
    const confirmPassword = this.form.controls['confirmPassword'].value;
    this.form.controls.password.setErrors(null);

    this.passwordRules.forEach((rule) => {
      if (rule.regex) {
        rule.valid = new RegExp(rule.regex, 'g').test(password);

        if (!rule.valid) {
          this.form.controls['password'].setErrors({
            [rule.key]: true,
          });
        }
      }
    });

    if (confirmPassword) {
      this.validateConfirmPasswordRules();
    }
  }

  validateConfirmPasswordRules(): void {
    const password = this.form.controls['password'].value;
    const confirmPassword = this.form.controls['confirmPassword'].value;
    this.form.controls.confirmPassword.setErrors(null);

    this.confirmPasswordRules.forEach((rule) => {
      rule.valid = Boolean(password === confirmPassword);

      if (!rule.valid) {
        this.form.controls['confirmPassword'].setErrors({
          [rule.key]: true,
        });
      }
    });
  }

  private verifyLastPassword(): void {
    const password = this.form.controls['password'].value;
    this.passwordRules[this.passwordRules.length - 1].valid = false;

    if (!password || (password && password.length < 8)) {
      return;
    }

    this.verifyingLastPassword.set(true);
    this._verifyUserPasswordService
      .handle({
        cityId: this.cityId,
        email: this.email,
        password,
      })
      .then(
        () => {
          this.passwordRules[this.passwordRules.length - 1].valid = false;
          this.form.controls['password'].setErrors({
            lastPassword: true,
          });
          this.verifyingLastPassword.set(false);
        },
        () => {
          this.passwordRules[this.passwordRules.length - 1].valid = true;
          this.verifyingLastPassword.set(false);
        }
      );
  }
}
